import {
  useState, useEffect, SyntheticEvent, useRef
} from 'react';
import { Label, crTheme } from '@comicrelief/component-library';
import { ThreeDSecureVerifyPayload } from 'braintree-web';

import { logger } from '@/services/logger/logger';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';
import { useCartContext } from '@/services/cart/CartContext';
import { ErrorCustomiser } from '@/services/error/ErrorContainer';
import {
  setComplete, clearErrorMessage, setErrorMessage, setShowRecaptchaV2
} from '@/features/paymentProcess/transactionSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';

import { ProviderLogos } from '@/components/providerLogos/ProviderLogos';
import SoftHider from '@/components/softHider/SoftHider';
import Loader from '@/components/loader/Loader';

import RecaptchaLibV3 from '@/libs/recaptcha-lib/components/RecaptchaLibV3';
import RecaptchaLibV2 from '@/libs/recaptcha-lib/components/RecaptchaLibV2';
import { RecaptchaLibToken } from '@/libs/recaptcha-lib/types/types';

import { useBraintreeCreate } from './useBraintreeCreate';
import { useBraintreeExecute } from './useBraintreeExecute';
import { useBraintreePayment } from './useBraintreePayment';
import StepBack from '@/components/stepBack/StepBack';
import { StyledButtonWithStates } from '../../components/global/Global.style';
import paymentFieldPlaceholders from '../stripe/StripeCardData';

import {
  ExpiryCvcWrapper,
  InnerExpiryCvcWrapper,
  ButtonWrapper
} from '../stripe/StripeCard.styles';
import { BraintreeFieldsElement, RecaptchaWrapper } from './BraintreeCard.styles';

interface StripeCardProps {
  currencySymbol: string;
}

function BraintreeCard({ currencySymbol }: StripeCardProps) {
  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const { cartData } = useCartContext();

  const journeyNavigate = useJourneyNavigate();
  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();
  const { selectedJourney } = useJourneyOptions();

  const cardNumberDiv = useRef<HTMLDivElement | null>(null);
  const cardCvvDiv = useRef<HTMLDivElement | null>(null);
  const cardExpiryDiv = useRef<HTMLDivElement | null>(null);

  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<RecaptchaLibToken>();
  const [formValid, setFormValid] = useState(false);
  const showReCaptchaV2 = useAppSelector(state => state.transaction.showReCaptchaV2);
  const prevShowV2Ref = useRef(showReCaptchaV2);

  const { triggerCreateApi, createData } = useBraintreeCreate();

  // Track previous showReCaptchaV2 value and handle version switching
  useEffect(() => {
    async function createApi() {
      // Wait for cart data to be loaded
      if (!cartData) {
        return;
      }

      // If switching from v3 to v2, clear the token first
      if (!prevShowV2Ref.current && showReCaptchaV2) {
        setRecaptchaToken(null);
        prevShowV2Ref.current = true;
        return; // Exit early to prevent API call during version switch
      }

      // Update ref for next time
      prevShowV2Ref.current = showReCaptchaV2;

      // Don't make API call if no token
      if (!recaptchaToken) {
        return;
      }

      await triggerCreateApi({
        recaptchaToken,
        reCaptchaVersion: showReCaptchaV2 ? 2 : 3,
        onSuccess: () => {},
        onError: (error?: unknown) => {
          const typedError = error as { status?: number };
          if (typedError?.status === 401 && !showReCaptchaV2) {
            logger.error('Failed reCaptcha v3 check');
            setRecaptchaToken(null);
            dispatch(setShowRecaptchaV2(true));
            return;
          }

          dispatch(setErrorMessage(ErrorCustomiser(error, 'Error initiating Braintree payment')));
          logger.error('Error initiating Braintree payment');
          journeyNavigate('error');
        },
      });
    }

    void createApi();
  }, [dispatch, journeyNavigate, triggerCreateApi, recaptchaToken, showReCaptchaV2, cartData]);

  // See: https://braintree.github.io/braintree-web/current/module-braintree-web_hosted-fields.html#~fieldOptions
  const hostedFieldsConfig = {
    number: {
      container: cardNumberDiv.current,
      placeholder: paymentFieldPlaceholders.cardNumber
    },
    expirationDate: {
      container: cardExpiryDiv.current,
      placeholder: paymentFieldPlaceholders.expiryDate
    },
    cvv: {
      container: cardCvvDiv.current,
      placeholder: paymentFieldPlaceholders.CVC
    },
  };

  // See: https://developer.paypal.com/braintree/docs/guides/hosted-fields/styling/javascript/v2
  // See: https://braintree.github.io/braintree-web/current/module-braintree-web_hosted-fields.html#~styleOptions
  const hostedFieldsStyles = {
    // NB: changes to these required an application cache clear
    input: {
      color: crTheme.color('grey_4'),
      'font-size': '1rem',
      'font-family': 'Montserrat, Impact, sans-serif',
    },
    '.invalid': {
      color: crTheme.color('red'),
    },
  };

  // Init Braintree payment, including loading Braintree hosted fields
  const { triggerPayment } = useBraintreePayment(
    hostedFieldsConfig,
    hostedFieldsStyles,
    setFormValid,
    {
      onSuccess: () => {
        setReady(true);
      },
      onError: () => {
        dispatch(setErrorMessage('Error initiating Braintree payment'));
        journeyNavigate('error');
      },
    }
  );

  // Handle payment form submit
  const { triggerExecuteApi } = useBraintreeExecute();

  const handleRecaptchaToken = (token: RecaptchaLibToken) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    setProcessing(true);

    // Prepare payment-service execute following Braintree payment success
    // Triggered from triggerPayment() onSuccess() callback (called below)
    const callExecuteApi = (threeDSecureResult: ThreeDSecureVerifyPayload) => {
      void triggerExecuteApi(
        threeDSecureResult?.nonce,
        {
          onSuccess: () => {
            setProcessing(false);
            dispatch(setComplete(true));
            dispatch(clearErrorMessage());
            paymentSuccess();
          },
          onError: (error) => {
            setProcessing(false);
            dispatch(setErrorMessage(ErrorCustomiser(error, 'Error processing payment')));
            paymentFailure();
          },
        },
      );
    };

    // Trigger Braintree payment on form submit
    void triggerPayment({
      onSuccess: callExecuteApi,
      onError: () => {
        setProcessing(false);
        dispatch(setErrorMessage('Error processing payment'));
        paymentFailure();
      },
    });
  };

  const shouldShowForm = !!(ready && !!createData && !processing);
  const isPayin = selectedJourney === 'payin';

  return (
    <>
      {/* Show reCAPTCHA v2 first if enabled */}
      {(showReCaptchaV2 && !recaptchaToken) ? (
        <RecaptchaWrapper>
          <RecaptchaLibV2 callback={handleRecaptchaToken} />
        </RecaptchaWrapper>
      ) : (
        <SoftHider show={shouldShowForm} loader={<Loader />}>
          <form onSubmit={event => void handleSubmit(event)}>

            <PaymentErrorText />

            <Label label="Card number" />
            <BraintreeFieldsElement ref={cardNumberDiv} />

            <ExpiryCvcWrapper>
              <InnerExpiryCvcWrapper>
                <Label label="Expiration date" />
                <BraintreeFieldsElement ref={cardExpiryDiv} />
              </InnerExpiryCvcWrapper>

              <InnerExpiryCvcWrapper>
                <Label label="CVC" />
                <BraintreeFieldsElement ref={cardCvvDiv} />
              </InnerExpiryCvcWrapper>
            </ExpiryCvcWrapper>

            <ProviderLogos />

            <ButtonWrapper>
              <StyledButtonWithStates
                type="submit"
                disabled={!formValid || processing}
                fullWidth
                data-test="cardPaymentContinue"
              >
                {isPayin ? 'Pay in' : 'Donate'}
                {' '}
                {currencySymbol}
                {paymentSelectState.amount}
                {' '}
                {isPayin ? 'now' : 'once'}
              </StyledButtonWithStates>
            </ButtonWrapper>

            {!showReCaptchaV2 && <RecaptchaLibV3 action="braintreeCard" callback={handleRecaptchaToken} />}

          </form>

          <StepBack data-test="cardPaymentGoBack" />

        </SoftHider>
      )}
    </>
  );
}

export default BraintreeCard;
